import {ContentContainer, useMessage} from '@coolcolduk/ui-mui';
import {useState} from 'react';
import {Job} from '@cp/model';
import ErrorComponent from '../../../components/ErrorComponent/ErrorComponent';
import {env} from '../../../config/env';
import useCleanupOldJobs from '../../../hooks/jobs/useCleanupOldJobs';
import useDeleteJob from '../../../hooks/jobs/useDeleteJob';
import useIncreaseJobRunLimit from '../../../hooks/jobs/useIncreaseJobRunLimit';
import useJobs from '../../../hooks/jobs/useJobs';
import useResumeJob from '../../../hooks/jobs/useResumeJob';
import LoadingPage from '../../other/LoadingPage';
import AdminJobsHeader from './AdminJobsHeader';
import AdminJobsList from './AdminJobsList';
import JobDetailDialog from './JobDetailDialog/JobDetailDialog';
import JobCleanupDialog from './JobManagementPage/JobCleanupDialog';
import JobDeleteDialog from './JobManagementPage/JobDeleteDialog';

export default function AdminJobsPage() {
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [deleteJobId, setDeleteJobId] = useState<string | null>(null);
  const [showCleanupDialog, setShowCleanupDialog] = useState(false);
  const [triggeringJobId, setTriggeringJobId] = useState<string | null>(null);
  const {showMessage} = useMessage();

  const {jobs, loading, error, refetch} = useJobs();
  const {deleteJob, loading: deleteLoading} = useDeleteJob();
  const {cleanupOldJobs, loading: cleanupLoading} = useCleanupOldJobs();
  const {resumeJob, loading: resumeLoading} = useResumeJob();
  const {increaseJobRunLimit, loading: increaseRunLimitLoading} = useIncreaseJobRunLimit();

  const handleJobClick = (job: Job) => {
    setSelectedJob(job);
  };

  const handleCloseDialog = () => {
    setSelectedJob(null);
  };

  const handleDeleteClick = (event: React.MouseEvent, jobId: string) => {
    event.stopPropagation();
    setDeleteJobId(jobId);
  };

  const handleResumeClick = async (event: React.MouseEvent, jobId: string) => {
    event.stopPropagation();
    try {
      await resumeJob(jobId);
      await refetch();
      showMessage('Job resumed successfully', 6000, 'success');
    } catch (err) {
      console.error('Error resuming job:', err);
      showMessage('Failed to resume job. Please try again.', 6000, 'error');
    }
  };

  const handleDeleteConfirm = async () => {
    if (deleteJobId) {
      try {
        await deleteJob(deleteJobId);
        await refetch();
        setDeleteJobId(null);
        showMessage('Job deleted successfully', 6000, 'success');
      } catch (err) {
        console.error('Error deleting job:', err);
        showMessage('Failed to delete job. Please try again.', 6000, 'error');
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteJobId(null);
  };

  const handleCleanupClick = () => {
    setShowCleanupDialog(true);
  };

  const handleCleanupConfirm = async () => {
    try {
      const deletedCount = await cleanupOldJobs();
      await refetch();
      setShowCleanupDialog(false);
      showMessage(`Successfully cleaned up ${deletedCount || 0} old jobs`, 6000, 'success');
    } catch (err) {
      console.error('Error cleaning up old jobs:', err);
      setShowCleanupDialog(false);
      showMessage('Failed to cleanup old jobs. Please try again.', 6000, 'error');
    }
  };

  const handleCleanupCancel = () => {
    setShowCleanupDialog(false);
  };

  const handleRefresh = async () => {
    try {
      await refetch();
      showMessage('Jobs list refreshed successfully', 6000, 'success');
    } catch (err) {
      console.error('Error refreshing jobs:', err);
      showMessage('Failed to refresh jobs list. Please try again.', 6000, 'error');
    }
  };

  const handleIncreaseRunLimit = async (event: React.MouseEvent, jobId: string, amount: number) => {
    event.stopPropagation();
    try {
      const updated = await increaseJobRunLimit(jobId, amount);
      await refetch();
      if (updated) {
        showMessage(`Run limit increased by ${amount}`, 6000, 'success');
      } else {
        showMessage('Job has no run limit or update failed', 6000, 'warning');
      }
    } catch (err) {
      console.error('Error increasing run limit:', err);
      showMessage(
        `Failed to increase run limit: ${err instanceof Error ? err.message : 'Unknown error'}`,
        6000,
        'error',
      );
    }
  };

  const handleTriggerJobProcessor = async (event: React.MouseEvent, jobId: string) => {
    event.stopPropagation();
    setTriggeringJobId(jobId);
    try {
      const response = await fetch(`${env.trigger.jobProcessor}?id=${jobId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      await refetch();
      showMessage('Job processor triggered successfully!', 6000, 'success');
    } catch (err) {
      console.error('Job processor error:', err);
      showMessage(`Job processor failed: ${err instanceof Error ? err.message : 'Unknown error'}`, 6000, 'error');
    } finally {
      setTriggeringJobId(null);
    }
  };

  if (loading) {
    return <LoadingPage />;
  }

  if (error) {
    return <ErrorComponent message={`Error loading jobs: ${error.message}`} />;
  }

  return (
    <ContentContainer data-testid="job-management-page">
      <AdminJobsHeader
        jobsCount={jobs.length}
        loading={loading}
        cleanupLoading={cleanupLoading}
        onRefresh={handleRefresh}
        onCleanupClick={handleCleanupClick}
      />

      <AdminJobsList
        jobs={jobs}
        deleteLoading={deleteLoading}
        resumeLoading={resumeLoading}
        increaseRunLimitLoading={increaseRunLimitLoading}
        onJobClick={handleJobClick}
        onDeleteClick={handleDeleteClick}
        onResumeClick={handleResumeClick}
        onIncreaseRunLimit={handleIncreaseRunLimit}
        onTriggerJobProcessor={env.isDev ? handleTriggerJobProcessor : undefined}
        triggeringJobId={env.isDev ? triggeringJobId : null}
      />

      <JobDetailDialog job={selectedJob} open={!!selectedJob} onClose={handleCloseDialog} />

      <JobDeleteDialog
        open={!!deleteJobId}
        deleteLoading={deleteLoading}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      />

      <JobCleanupDialog
        open={showCleanupDialog}
        cleanupLoading={cleanupLoading}
        onCancel={handleCleanupCancel}
        onConfirm={handleCleanupConfirm}
      />
    </ContentContainer>
  );
}
